import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Route,
  MapPin,
  CheckCircle2,
  Edit3,
  Save,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default icons for tour stops
const STOP_ICONS = ['🎯', '📍', '🔍', '⭐', '🏭', '🔧', '📊', '✅'];

export function GuidedTour({ 
  isOpen,
  onClose,
  sweeps = [],
  onNavigate,
  currentSweep,
  transitionSpeed = 1.5,
}) {
  const { token, canEdit } = useAuth();
  const { currentTour } = useTour();
  
  const [navPoints, setNavPoints] = useState([]);
  const [tourStops, setTourStops] = useState([]);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tourComplete, setTourComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [isExpanded, setIsExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch nav points from database
  const fetchNavPoints = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/navpoints/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setNavPoints(data);
        
        // Convert nav points to tour stops
        const stops = data.map((np, index) => ({
          id: np.id,
          name: np.tour_title || np.label || `Stop ${index + 1}`,
          description: np.tour_description || np.description || '',
          sweepId: np.sweep_id,
          rotation: np.rotation,
          zoom: np.zoom,
          icon: STOP_ICONS[index % STOP_ICONS.length],
          originalLabel: np.label,
        }));
        
        setTourStops(stops);
      }
    } catch (err) {
      console.error('Failed to fetch nav points:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Load nav points when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNavPoints();
      setCurrentStopIndex(0);
      setIsPlaying(false);
      setTourComplete(false);
    }
  }, [isOpen, fetchNavPoints]);

  const currentStop = tourStops[currentStopIndex];
  const progress = tourStops.length > 0 ? ((currentStopIndex + 1) / tourStops.length) * 100 : 0;

  const goToNextStop = useCallback(() => {
    if (currentStopIndex < tourStops.length - 1) {
      setCurrentStopIndex(prev => prev + 1);
    }
  }, [currentStopIndex, tourStops.length]);

  const goToPrevStop = useCallback(() => {
    if (currentStopIndex > 0) {
      setCurrentStopIndex(prev => prev - 1);
      setTourComplete(false);
    }
  }, [currentStopIndex]);

  // Auto-advance when playing
  useEffect(() => {
    if (!isPlaying || tourComplete || tourStops.length === 0) return;

    const timer = setTimeout(() => {
      if (currentStopIndex < tourStops.length - 1) {
        goToNextStop();
      } else {
        setIsPlaying(false);
        setTourComplete(true);
      }
    }, transitionSpeed * 1000 + 4000); // transition + viewing time

    return () => clearTimeout(timer);
  }, [isPlaying, currentStopIndex, transitionSpeed, tourComplete, goToNextStop, tourStops.length]);

  // Navigate when stop changes
  useEffect(() => {
    if (!isOpen || !currentStop || !onNavigate) return;
    
    if (currentStop.sweepId) {
      console.log(`Guided Tour: Moving to ${currentStop.name} (${currentStop.sweepId})`);
      console.log('Guided Tour: rotation:', currentStop.rotation, 'zoom:', currentStop.zoom);
      onNavigate(currentStop.sweepId, { 
        rotation: currentStop.rotation,
        zoom: currentStop.zoom
      });
    }
  }, [isOpen, currentStopIndex, currentStop, onNavigate]);

  const togglePlay = () => {
    if (tourStops.length === 0) return;
    
    if (tourComplete) {
      setCurrentStopIndex(0);
      setTourComplete(false);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleClose = () => {
    setIsPlaying(false);
    setCurrentStopIndex(0);
    setTourComplete(false);
    setEditingStop(null);
    onClose();
  };

  const goToStop = (index) => {
    setCurrentStopIndex(index);
    setIsPlaying(false);
    setTourComplete(false);
  };

  // Edit tour stop title/description
  const startEditing = (stop, e) => {
    e.stopPropagation();
    setEditingStop(stop.id);
    setEditForm({
      name: stop.name,
      description: stop.description || ''
    });
  };

  const cancelEditing = () => {
    setEditingStop(null);
    setEditForm({ name: '', description: '' });
  };

  const saveStopEdit = async (stopId) => {
    if (!token) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/navpoints/${stopId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tour_title: editForm.name,
          tour_description: editForm.description
        })
      });
      
      if (res.ok) {
        // Update local state
        setTourStops(prev => prev.map(stop => 
          stop.id === stopId 
            ? { ...stop, name: editForm.name, description: editForm.description }
            : stop
        ));
        setEditingStop(null);
        console.log('Tour stop saved successfully');
      } else {
        const errorText = await res.text();
        console.error('Failed to save:', res.status, errorText);
      }
    } catch (err) {
      console.error('Failed to update tour stop:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 w-[380px] max-w-[90vw]">
      <div className="bg-background/95 backdrop-blur-md border rounded-xl shadow-2xl overflow-hidden">
        {/* Header - Always visible */}
        <div 
          className="flex items-center justify-between p-3 border-b cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <Route className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Guided Tour</span>
            {tourStops.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {currentStopIndex + 1}/{tourStops.length}
              </Badge>
            )}
            {isPlaying && (
              <Badge variant="secondary" className="text-xs animate-pulse">
                Playing
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon-sm" 
              onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon-sm" 
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Expandable content */}
        {isExpanded && (
          <div className="p-3 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : tourStops.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">No Tour Stops</p>
                <p className="text-xs mt-1">Add View Positions first</p>
              </div>
            ) : (
              <>
                {/* Progress bar */}
                <div className="space-y-1">
                  <Progress value={progress} className="h-1.5" />
                </div>

                {/* Current Stop */}
                <div className={cn(
                  "p-3 rounded-lg border transition-all",
                  tourComplete ? "border-green-500 bg-green-500/10" : "border-primary/50 bg-primary/5"
                )}>
                  {editingStop === currentStop?.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Stop title"
                        className="text-sm h-8"
                        autoFocus
                      />
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Description"
                        rows={2}
                        className="text-xs resize-none"
                      />
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="h-7 text-xs"
                          onClick={() => saveStopEdit(currentStop.id)}
                          disabled={saving}
                        >
                          {saving ? (
                            <>Saving...</>
                          ) : (
                            <><Save className="h-3 w-3 mr-1" /> Save</>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={cancelEditing}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{currentStop?.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <h4 className="font-medium text-sm truncate">{currentStop?.name}</h4>
                          {canEdit && (
                            <Button 
                              variant="ghost" 
                              size="icon-sm"
                              className="h-5 w-5 shrink-0"
                              onClick={(e) => startEditing(currentStop, e)}
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        {currentStop?.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{currentStop.description}</p>
                        )}
                      </div>
                      {tourComplete && <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />}
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={goToPrevStop}
                    disabled={currentStopIndex === 0}
                  >
                    <SkipBack className="h-3 w-3" />
                  </Button>

                  <Button
                    variant={isPlaying ? "secondary" : "default"}
                    size="sm"
                    onClick={togglePlay}
                    className="px-6"
                  >
                    {tourComplete ? (
                      <><Play className="h-3 w-3 mr-1" /> Restart</>
                    ) : isPlaying ? (
                      <><Pause className="h-3 w-3 mr-1" /> Pause</>
                    ) : (
                      <><Play className="h-3 w-3 mr-1" /> {currentStopIndex === 0 ? 'Start' : 'Resume'}</>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={goToNextStop}
                    disabled={currentStopIndex === tourStops.length - 1}
                  >
                    <SkipForward className="h-3 w-3" />
                  </Button>
                </div>

                {/* Stop indicators */}
                <div className="flex justify-center gap-1.5 flex-wrap">
                  {tourStops.map((stop, index) => (
                    <button
                      key={stop.id}
                      onClick={() => goToStop(index)}
                      className={cn(
                        "w-2.5 h-2.5 rounded-full transition-all",
                        index === currentStopIndex 
                          ? "bg-primary scale-125" 
                          : index < currentStopIndex 
                            ? "bg-green-500" 
                            : "bg-muted hover:bg-muted-foreground/50"
                      )}
                      title={stop.name}
                    />
                  ))}
                </div>

                {/* Stops list (collapsible) */}
                <details className="group">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                    <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                    All stops
                  </summary>
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {tourStops.map((stop, index) => (
                      <button
                        key={stop.id}
                        onClick={() => goToStop(index)}
                        className={cn(
                          "w-full flex items-center gap-2 p-1.5 rounded text-left transition-all text-xs",
                          index === currentStopIndex 
                            ? "bg-primary/10 text-primary" 
                            : "hover:bg-secondary"
                        )}
                      >
                        <span>{stop.icon}</span>
                        <span className="flex-1 truncate">{stop.name}</span>
                        {index < currentStopIndex && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                        {index === currentStopIndex && <MapPin className="h-3 w-3 text-primary" />}
                      </button>
                    ))}
                  </div>
                </details>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GuidedTour;
